
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileText, Download } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { apiBaseUrl } from "@/lib/api-client";

type PDFMaterial = {
  id: string;
  display_name_et: string;
  s3_object_key: string;
};

type LearnerMaterialsSectionProps = {
  pdfMaterials: PDFMaterial[];
};

const LearnerMaterialsSection = ({
  pdfMaterials
}: LearnerMaterialsSectionProps) => {
  const [loadingPdfId, setLoadingPdfId] = useState<string | null>(null);

  const handleOpenPdf = async (displayName: string, materialId: string) => {
    try {
      setLoadingPdfId(materialId);

      // Show loading toast
      const loadingToastId = toast({
        title: "Laadimine",
        description: `PDF-i "${displayName}" laadimine...`,
      }).id;

      // GET /api/learner/material/:id/download — server resolves the file key
      // from the DB and streams the PDF. Auth is via Better Auth session cookie.
      const response = await fetch(`${apiBaseUrl}/api/learner/material/${materialId}/download`, {
        method: "GET",
        credentials: "include",
      });
      
      if (!response.ok) {
        // Try to get error details
        let errorMessage = "Failed to get PDF";
        try {
          const errorData = await response.json();
          console.error("Error response:", errorData);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          console.error("Error response text:", errorText);
        }
        throw new Error(errorMessage);
      }
      
      // Create blob from response and open in new tab
      const blob = await response.blob();
      
      // Verify that the blob is a PDF (basic check)
      if (blob.type !== "application/pdf" && !blob.type.includes("pdf")) {
        console.warn("Response is not a PDF file. Content type:", blob.type);
        if (blob.size < 1000) {
          // Small enough to be text, let's check
          const text = await blob.text();
          console.error("Small non-PDF response content:", text);
          throw new Error("Server returned invalid PDF format");
        }
      }
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Remove loading toast
      // @ts-ignore - toast dismiss is available
      window.clearTimeout(loadingToastId);
      
      // Open in new window
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      
      if (!newWindow) {
        toast({
          title: "Viga",
          description: "Popup-blocker võib takistada PDF-i avamist. Luba hüpikaknad.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Edu",
          description: `PDF "${displayName}" avatud uues aknas`,
          variant: "default"
        });
        
        // If the new window loaded properly, clean up the blob URL
        newWindow.addEventListener('load', () => {
          console.log("New window loaded, blob URL can be revoked soon");
        });
      }
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(url);
          console.log("Blob URL revoked");
        } catch (e) {
          console.error("Error revoking blob URL:", e);
        }
      }, 60000); // Longer timeout to ensure PDF is loaded
    } catch (error: any) {
      console.error("Error opening PDF:", error);
      toast({
        title: "Viga",
        description: error.message || "PDF faili avamine ebaõnnestus",
        variant: "destructive"
      });
    } finally {
      setLoadingPdfId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Kursuse materjalid</h2>
      </div>

      {pdfMaterials.length === 0 ? (
        <p className="text-gray-500">Ühtegi dokumenti veel pole</p>
      ) : (
        <ul className="space-y-2">
          {pdfMaterials.map((material) => (
            <li 
              key={material.id} 
              className="flex items-center justify-between p-3 border rounded-md bg-gray-50"
            >
              <div className="flex items-center">
                <FileText className="h-5 w-5 mr-3 text-kaija-accent" />
                <span>{material.display_name_et}</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleOpenPdf(material.display_name_et, material.id)}
                disabled={loadingPdfId === material.id}
              >
                {loadingPdfId === material.id ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-kaija-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Laadimine...
                  </span>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Ava
                  </>
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LearnerMaterialsSection;
